import { MorningAndNightProductType } from '@cureskin/api-client/src/table/regimen';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ApiClientConstant, ParseKeys, RequestQueryPayload, Table } from '@cureskin/api-client';
import moment from 'moment';
import { ConnectionService } from '../../services/connection-service';
import { AppConfig } from '../../app/app.config';
import { ProductInstructionSelectionModal } from './product-instruction-selection-popup';
import { Broadcaster } from '../broadcaster';
import { LocalStorage } from '../../services/local-storage-service';
import { DataUploadModal } from '../modal/dataUploadModal';
import { HelperService } from '../../services/helper-service';
import { RegimenSopServices } from '../../services/regimen-sop';
import { FollowUpSopChangeType } from '@cureskin/regimen-sop';
import { FollowUpSopCallback } from '../../services/regimen-sop/callbacks';
import { OnHoldPopupComponent } from '../onHoldPopup';
import { ServerAPIRequest, ServerAPIResponse } from '../../app/server-api.constants';
import { OnHoldDetailsOnHoldTypeEnum } from '@cureskin/api-client/src/server-api';

interface FollowUpSopChange {
  type: string;
  position: number;
  partOfDay: string;
  instruction: any;
  productId: string;
}

@Component({
    selector: 'regimen-products-edit',
    templateUrl: './regimen-products-edit.html',
    styleUrls: ['./regimen-products-edit.scss'],
    standalone: false
})
export class RegimenProductsEditComponent {
  public autoCompleteProductController: UntypedFormControl = new UntypedFormControl();
  productOptions: Observable<Array<{ name: string; object: any }>>;
  morningProducts: Array<any> = [];
  originalMorningProducts: Array<MorningAndNightProductType> = [];
  nightProducts: Array<any> = [];
  originalNightProducts: Array<MorningAndNightProductType> = [];
  regimenChange: any = {};
  isRegimenSave: boolean = false;
  isSelectedRegimen: boolean;
  showRegimenEditButton: boolean = true;
  changedProductsWhichAreAlreadyInOrdersInTransit: any = [];
  changedProductsWhichAreAlreadyInOrdersInDelivered: any = [];
  selectedSopRegimen: any;
  progressiveInfo: any;
  currentUser: Table.User;
  onHoldProductsPayload: ServerAPIRequest['OnHoldProducts'] = {
    onHoldProducts: [],
    onHoldDurationInDays: 0,
    ticketId: '',
    onHoldType: OnHoldDetailsOnHoldTypeEnum.FOLLOW_UP
  };
  originalProductIds: Set<string> = new Set();

  @Input('disableEdit') disableEdit: boolean;
  @Input('orderApprovalPage') orderApprovalPage: boolean;
  @Input('isEditAllowed') isEditAllowed: boolean;
  @Output('afterSaveRegimen') afterSaveRegimen: EventEmitter<any> = new EventEmitter();
  @Output('regimenUpdated') regimenUpdated: EventEmitter<any> = new EventEmitter();
  @Output('enableFinishAndSendButton') enableFinishAndSendButton: EventEmitter<any> = new EventEmitter();
  @Output() isRegimenSelected: EventEmitter<any> = new EventEmitter();
  @Input('regimenList') regimenList: Array<any>;
  @Input('regimen') regimen: any;
  @Input('user') user: Table.User;
  @Input('npdExperimentEnabled') npdExperimentEnabled: boolean;
  @Input('medicalNoteAdded') medicalNoteAdded: boolean;
  @Input('userSelectedVariantId') userSelectedVariantId: string;
  @Output('variantNotFound') variantNotFound: EventEmitter<any> = new EventEmitter();
  constructor(public appConfig: AppConfig,
    private dialog: MatDialog,
    private broadcaster: Broadcaster,
    private localStorage: LocalStorage,
    private connectionService: ConnectionService,
    private readonly regimenSopServices: RegimenSopServices,
    private helper: HelperService) {}

  async ngOnInit(): Promise<void> {
    this.productOptions = this.autoCompleteProductController.valueChanges
      .pipe(
        debounceTime(300),
        filter((token: string) => !!token.length),
        mergeMap((token: string) => this.getProducts(token)));
    await this.updateRegimenDetails();
    this.regimenList = this.regimenList?.filter((regimen: any, index: number): any => regimen.id !== this.regimen.id);
    if (this.regimenList?.length === 1) {
      await this.onChangeRegimen(this.regimenList[0]);
    }
    this.currentUser = this.connectionService.getCurrentUser();
  }

  async updateRegimenDetails(): Promise<void> {
    if (this.orderApprovalPage && this.regimenList?.length === 1) return;
    const where: any = {};
    if (this.regimen.get('regimenId')) {
      where.regimenId = this.regimen.get('regimenId');
    } else {
      where.objectId = this.regimen.id;
    }
    const regimen = await this.connectionService.findOneRegimen({
      where,
      include: [
        'products',
        'morning.product' as 'morning',
        'night.product' as 'night',
        'morning.product.instructions.frequencyOfApplicationLanguageString' as 'morning',
        'night.product.instructions.frequencyOfApplicationLanguageString' as 'night',
        'morning.product.instructions.areaOfApplicationLanguageString' as 'morning',
        'night.product.instructions.areaOfApplicationLanguageString' as 'night',
        'morning.product.instructions.quantityUnitLanguageString' as 'morning',
        'night.product.instructions.quantityUnitLanguageString' as 'night',
        'morning.product.instructions.durationOfApplicationLanguageString' as 'morning',
        'night.product.instructions.durationOfApplicationLanguageString' as 'night',
        'morning.instructionSet.frequencyOfApplicationLanguageString' as 'morning',
        'night.instructionSet.frequencyOfApplicationLanguageString' as 'night',
        'morning.instructionSet.areaOfApplicationLanguageString' as 'morning',
        'night.instructionSet.areaOfApplicationLanguageString' as 'night',
        'morning.instructionSet.quantityUnitLanguageString' as 'morning',
        'night.instructionSet.quantityUnitLanguageString' as 'night',
        'morning.instructionSet.durationOfApplicationLanguageString' as 'morning',
        'night.instructionSet.durationOfApplicationLanguageString' as 'night',
        'variants.products' as 'variants',
      ],
      project: ['morning', 'night', 'products', 'class', 'variants', 'selectedVariantId'],
    });
    this.regimen.set('morning', regimen.get('morning'));
    this.regimen.set('night', regimen.get('night'));
    this.regimen.set('products', regimen.get('products'));
    this.morningProducts = [...this.regimen.get('morning')];
    this.originalMorningProducts = this.helper.deepCopy(regimen.get('morning'));
    this.nightProducts = [...this.regimen.get('night')];
    this.originalNightProducts = this.helper.deepCopy(regimen.get('night'));
    this.originalProductIds = new Set(this.regimen.get('products').map(product => product.id));

    this.regimenChange.concernsLanguageString = this.regimen?.get('concernsLanguageString');
    this.regimenChange.originalProducts = [
      ...this.regimen.get('morning').map((morningProduct: any) => ({ ...morningProduct })),
      ...this.regimen.get('night').map((nightProduct: any) => ({ ...nightProduct })),
    ];
  }

  addNewProduct(partOfDay: 'morning' | 'night'): void {
    if (!this.checkRegimenEditAllowedUnderNPD()) return;
    const partOfDayName = `${partOfDay}Products`;
    this[partOfDayName].push({});
  }

  removeProduct(index: number, partOfDay: 'morning' | 'night'): void {
    if (!this.checkRegimenEditAllowedUnderNPD()) return;
    const partOfDayName = `${partOfDay}Products`;
    this[partOfDayName].splice(index, 1);
  }

  editProductInstruction(index: number, partOfDay: 'morning' | 'night'): void {
    if (!this.checkRegimenEditAllowedUnderNPD()) return;
    const partOfDayName = `${partOfDay}Products`;
    const dialogRef = this.dialog.open(ProductInstructionSelectionModal, {
      width: '95%',
      data: { product: this[partOfDayName][index].product,
        selectedInstructionCreationId: this[partOfDayName][index]?.instructionSet?.creationId,
        partOfDay: partOfDay.toUpperCase() },
    });
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (!result?.selectedInstructionCreationId) {
        this.broadcaster.broadcast('NOTIFY', { message: 'Instruction not chosen', type: this.appConfig.Shared.Toast.Type.ERROR });
        return;
      }
      this[partOfDayName][index].product = result.product;
      const instructionSet = this[partOfDayName][index].product.get('instructions')
        .find((instruction: any) => instruction.creationId === result.selectedInstructionCreationId);
      this[partOfDayName][index].instructionSet = instructionSet;
      this[partOfDayName][index].productName = this.getProductDisplayName(result.product);
      this[partOfDayName][index].purposeDescriptionLanguageString = result.product.get('purposeDescriptionLanguageString');
      this.autoCompleteProductController.setValue('');
    });
  }

  autoCompleteOnProductSelect(item: { name: string; object: any }, partOfDay: 'morning' | 'night', index: number): void {
    const partOfDayName = `${partOfDay}Products`;
    this[partOfDayName][index].product = item.object;
    this[partOfDayName][index].productName = item.name;
    this[partOfDayName][index].purposeDescriptionLanguageString = item.object.get('purposeDescriptionLanguageString');
    this.editProductInstruction(index, partOfDay);
  }

  async getProducts(name: string): Promise<Array<{ name: string; object: any }>> {
    const payload: RequestQueryPayload<Table.Catalog> = {
      where: {
        title: { $regex: name, $options: 'i' },
        inventoryStatus: ['AVAILABLE', 'UNAVAILABLE', 'RESTRICTED'],
      },
      include: ['instructions.frequencyOfApplicationLanguageString' as 'instructions',
        'instructions.frequencyOfApplicationLanguageString' as 'instructions',
        'instructions.areaOfApplicationLanguageString' as 'instructions',
        'instructions.areaOfApplicationLanguageString' as 'instructions',
        'instructions.quantityUnitLanguageString' as 'instructions',
        'instructions.quantityUnitLanguageString' as 'instructions'],
      project: ['title', 'quantity', 'quantityUnit', 'type', 'margUnit', 'mrp', 'price', 'instructions',
        'purposeDescriptionLanguageString', 'isRepair'],
      ascending: 'margUnit',
      limit: 10,
    };
    const userRoles = this.localStorage.getJsonValue('userRoles') || [];
    if (!userRoles.includes('adminDoctor')) {
      payload.where.mrp = { $ne: 0 };
    }
    const products = await this.connectionService.findCatalogs(payload);
    return products.map((product: any): { name: string; object: any } => ({
      name: this.getProductDisplayName(product),
      object: product,
    }));
  }

  getProductDisplayName(product: any): string {
    return `${product.get('title')} [ ${
      (product.get('margUnit') || 1) > 1
        ? `${product.get('margUnit')} sheets, `
        : ''
    }${product.get('quantity')}${product.get('quantityUnit')} ] ${product.get('type') === 'sample' ? '(sample)' : ''}`;
  }

  async openOnHoldPopup(): Promise<boolean> {
    const dialogRef = this.dialog.open(OnHoldPopupComponent, {
      width: '40%',
      data: { onHoldType: OnHoldDetailsOnHoldTypeEnum.FOLLOW_UP},
    });
    return new Promise<boolean>(() => {
      dialogRef.afterClosed().subscribe(async (result: any) => {
        if (result && result.numberOfDays) {
          this.onHoldProductsPayload.onHoldDurationInDays = result.numberOfDays;
          await this.saveRegimen();
        } else {
          this.broadcaster.broadcast('NOTIFY', { message: 'invalid onHold days', type: this.appConfig.Shared.Toast.Type.ERROR });
          this.enableFinishAndSendButton.emit();
          throw new Error('invalid onHold days');
        }
      });
    });
  }

  addProductInOnHoldProductsPayload(onHoldProducts: Table.Regimen['attributes']['morning']): void {
    onHoldProducts.forEach((onHoldProduct) => {
      this.onHoldProductsPayload.onHoldProducts.push({
        onHold: true,
        productId: onHoldProduct.product.id,
        instructionCreationId: onHoldProduct.instructionSet.creationId,
        partOfDay: onHoldProduct.instructionSet.partOfDay,
      });
    })
  }

  async saveRegimen(): Promise<void> {
    if (!this.regimen) return;
    this.regimen.set('morning', this.morningProducts);
    this.regimen.set('night', this.nightProducts);
    const regimenValidity: { valid: boolean, errorMessage?: string} = await this.checkCurrentRegimenIsValid();
    if (!regimenValidity.valid) {
      await Promise.reject(new Error(regimenValidity.errorMessage));
    }
    await this.updateOnHoldDetails();
    await this.regimen.save();
    this.afterSaveRegimen.emit(this.regimen);
  }

  async upsertOnHoldProducts(
    regimenObjectId: string,
    onHoldProductsInput: ServerAPIRequest['OnHoldProducts'],
  ): Promise<ServerAPIResponse['UpsertOnHoldProducts']> {
    const userActivityData = await this.connectionService.serverApi.upsertOnHoldProductsInRegimen(
      this.connectionService.getCurrentUser().getSessionToken(),
      regimenObjectId,
      onHoldProductsInput,
    );
    return userActivityData;
  }

  updateRegimenFromAPIResponse(morningCopy: any[] = [], nightCopy: any[] = [], morning: any[] = [], night: any[] = []): void {
    const getProductId = (item: any): string => (item?.productId || item?.product?.id || item?.product?.objectId);
    const getPartOfDay = (item: any): string => (item?.partOfDay || item?.instructionSet?.partOfDay);
    const makeKey = (item: any): string => `${getProductId(item)}__${getPartOfDay(item)}`;

    const updatedKeys = new Set(
      (this.onHoldProductsPayload?.onHoldProducts || [])
        .map((p: any) => `${p?.productId}__${p?.partOfDay}`)
    );
    // Build final arrays from local regimen; replace on-hold matches with API items
    const rebuildFromLocal = (apiArr: any[], localArr: any[]): void => {
      if (!Array.isArray(apiArr) || !Array.isArray(localArr)) return;
      const apiMap = new Map<string, any>(apiArr.map((s: any) => [makeKey(s), s]));
      const finalArr: any[] = [];
      for (let i = 0; i < localArr.length; i += 1) {
        const localItem = localArr[i];
        const key = makeKey(localItem);
        if (updatedKeys.has(key) && apiMap.has(key)) {
          // For on-hold items, replace with API item entirely to keep server fields
          finalArr.push({...apiMap.get(key), instructionSet: localItem.instructionSet, product: localItem.product});
        } else {
          finalArr.push(localItem);
        }
      }
      apiArr.length = 0;
      for (let i = 0; i < finalArr.length; i += 1) apiArr.push(finalArr[i]);
    };

    rebuildFromLocal(morningCopy, morning);
    rebuildFromLocal(nightCopy, night);
  }

  async updateOnHoldDetails(): Promise<void> {
    if (this.regimenList?.length) {
      return;
    }
    const onHoldProducts = [...this.morningProducts, ...this.nightProducts].filter(product=> product.onHold);
    const newProductAddedInRegimen = [...this.morningProducts, ...this.nightProducts].filter(product=> product.onHold)
      .some((each: any) => !this.originalProductIds.has(each.product.id));
    if (this.regimen.get('type') === ApiClientConstant.Regimen.Type.PERSONALIZED
      && this.currentUser.get('type') === ApiClientConstant.User.Type.DOCTOR
      && !newProductAddedInRegimen && onHoldProducts.length) {
      if (!this.onHoldProductsPayload.onHoldDurationInDays) {
        await this.openOnHoldPopup();
        throw Error('onHold duration not added')
      }
      this.addProductInOnHoldProductsPayload(onHoldProducts);
      const response = await this.upsertOnHoldProducts(this.regimen.id, this.onHoldProductsPayload);
      if (response.status === 200) {
        const morningCopy = response.data.data.regimen.morning;
        const nightCopy = response.data.data.regimen.night;
        this.updateRegimenFromAPIResponse(morningCopy, nightCopy, this.regimen.get('morning'), this.regimen.get('night'))
        this.regimen.set('morning', morningCopy);
        this.regimen.set('night', nightCopy);
      } else {
        this.broadcaster.broadcast('NOTIFY', { message: 'Failed to update on hold products', type: this.appConfig.Shared.Toast.Type.ERROR });
      }
    }
  }

  async checkCurrentRegimenIsValid(): Promise<{ valid: boolean, errorMessage?: string}> {
    // should not contain empty products in regimen
    if (this.doesContainEmptyObject(this.regimen.get('morning')) || this.doesContainEmptyObject(this.regimen.get('night'))) {
      return { valid: false, errorMessage: 'Product can not be empty in any regimen' };
    }

    // should check for product limit
    const uniqueProducts = this.findUniqueProducts(this.regimen);
    if (['variant_1999', 'variant_4999', 'variant_2999'].includes(this.regimen?.get('selectedVariantId'))
      && uniqueProducts.length !== 5 && !this.regimen.get('active')) {
      this.broadcaster.broadcast('NOTIFY', {
        message: '5 products to be sent for the current variant',
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
      return { valid: false, errorMessage: 'Regimen should have 5 products' };
    }

    // check instructions are valid or not
    const checkInstructionsAreValid = await this.checkInstructionsAreValid();
    if (!checkInstructionsAreValid) {
      return { valid: false, errorMessage: 'Two or more alternate day products have same days of application' };
    }

    // check for display concern changed after changing repair product
    const isRepairProductEditedButDisplayConcernNotChanged = await this.isRepairProductEditedButDisplayConcernNotChanged();
    if (!isRepairProductEditedButDisplayConcernNotChanged) {
      return { valid: false, errorMessage: 'Display Concern not changed' };
    }
    return { valid: true };
  }

  doesContainEmptyObject(regimens: Array<any>): boolean {
    return !!regimens.filter((value: any) => Object.keys(value).length === 0).length;
  }

  async checkChangedProductsHasAlreadyInOrder(): Promise<void> {
    try {
      const startDate = moment().subtract(15, 'day').startOf('day').add(6, 'hours')
        .toDate();
      const endDate = moment().startOf('day').add(1, 'day').toDate();
      const changedProducts = this.getChangedProductsInCurrentRegimen();
      const where: any = {};
      where.user = this.regimen.get('forUser');
      where.createdAt = { $gte: startDate, $lte: endDate };
      where.isPartialOrder = false;
      where.products = { $in: [...changedProducts] };
      where.stage = { $nin: ['RETURN_INITIATED',
        'RETURN_REQUESTED',
        'ONLINE_PAYMENT_PENDING',
        'ONLINE_PAYMENT_SUCCESS',
        'INITIAL',
        'RETURNED',
        'CANCELED',
        'LOST_IN_TRANSIT'] };
      const orders = await this.connectionService.findOrders({ where, project: ['products', 'stage'] });
      if (!orders.length) return;
      orders.forEach((order:any) => {
        this.segregateDeliveredAndInTransitOrders(order, changedProducts);
      });
    } catch (error) {
      this.changedProductsWhichAreAlreadyInOrdersInDelivered = [];
      this.changedProductsWhichAreAlreadyInOrdersInTransit = [];
    }
  }

  async segregateDeliveredAndInTransitOrders(order: any, changedProducts: Array<any>): Promise<void> {
    const changedProductsIds: Array<string> = changedProducts.map((product: any) => product.id);
    // todo complete this function
    order.get('products').forEach((product: any) => {});
  }

  getChangedProductsInCurrentRegimen(): Array<any> {
    const regimenMorningProduct = this.regimen.get('morning').map((product:any) => product.product.get('margId'));
    const regimenNightProduct = this.regimen.get('night').map((product:any) => product.product.get('margId'));
    const editedRegimenAllProductIds:any = [...regimenMorningProduct, ...regimenNightProduct];
    const oldRegimenAllProductIds:any = this.regimen.get('products').map((product:any) => product.product.margId);
    return editedRegimenAllProductIds.filter((item:any) => !oldRegimenAllProductIds.includes(item));
  }
  async checkInstructionsAreValid(): Promise<boolean> {
    const productsInRegimen: Array<any> = this.regimen.get('morning').concat(this.regimen.get('night'));
    const map = {};
    const whenToApply = productsInRegimen
      .map((item: any) => {
        if (!item.instructionSet.frequencyOfApplicationLanguageString?.get('en')) return null;
        return item.instructionSet.frequencyOfApplicationLanguageString.get('en').split(',');
      })
      .filter((item: any) => item !== null)
      .filter((item: any) => item.includes('Monday') || item.includes('Tuesday'));
    const isDuplicate: boolean = whenToApply.some((item: any): boolean => item.some((i: any) => {
      const bool = !!map[i];
      map[i] = true;
      return bool;
    }));
    if (isDuplicate) return confirm('Two or more alternate day products have same days of application. are you sure?');
    return true;
  }

  async isRepairProductEditedButDisplayConcernNotChanged(): Promise<boolean> {
    if (!this.regimenChange?.originalProducts) return true;
    let isRepairProductsChanged = false;
    const repairProductsIdsBeforeChangeRegimen = [];
    const repairProductsIdsAfterChangeRegimen = [];
    this.regimenChange.originalProducts.forEach((product: any) => {
      if (product?.product?.get('isRepair')) {
        repairProductsIdsBeforeChangeRegimen.push(product.product.id);
      }
    });
    [...this.regimen.get('morning'), ...this.regimen.get('night')].forEach((product: any) => {
      if (product?.product?.get('isRepair')) {
        repairProductsIdsAfterChangeRegimen.push(product.product.id);
      }
    });
    repairProductsIdsBeforeChangeRegimen.forEach((productId: string) => {
      if (!repairProductsIdsAfterChangeRegimen.includes(productId)) {
        isRepairProductsChanged = true;
      }
    });
    if ((isRepairProductsChanged || repairProductsIdsBeforeChangeRegimen.length !== repairProductsIdsAfterChangeRegimen.length)
      && this.isSameArrayOfParseObject(this.regimen.get('concernsLanguageString'), this.regimenChange.concernsLanguageString)) {
      return confirm('Are you sure to continue without changing display concern?');
    }
    return true;
  }

  private isSameArrayOfParseObject(parseObjectArray1: Array<any> = [], parseObjectArray2: Array<any> = []): boolean {
    if (parseObjectArray1.length !== parseObjectArray2.length) {
      return false;
    }
    return parseObjectArray1.every((item: any, index: number) => (item.id === parseObjectArray2[index].id));
  }

  async saveRegimenOrderApproval(updatedProgressiveInfo?: any, skipCreatingOrderForNewProducts: boolean = false): Promise<void> {
    if (!this.regimen) return;
    this.regimen.set('morning', this.morningProducts);
    this.regimen.set('night', this.nightProducts);
    if (updatedProgressiveInfo && Object.keys(updatedProgressiveInfo).length > 0) {
      this.regimen.set('progressiveInfo', [updatedProgressiveInfo]);
    }
    if (skipCreatingOrderForNewProducts)  {
      this.regimen.set('placeOrderForNewProduct', false);
    }
    this.disableFollowUpSopOnRegimenEdit();
    try {
      await this.regimen.save();
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: 'Error in saving regimen', type: this.appConfig.Shared.Toast.Type.ERROR });
      return;
    }
    this.isRegimenSave = true;
    this.afterSaveRegimen.emit(this.regimen);
    this.broadcaster.broadcast('NOTIFY', { message: 'Regimen Saved', type: this.appConfig.Shared.Toast.Type.SUCCESS });
    // this.disableEdit = true;
  }

  private disableFollowUpSopOnRegimenEdit(): void {
    if (!this.selectedSopRegimen) return;
    if (this.areProductsSame(this.selectedSopRegimen.get('morning'), this.morningProducts)
      && this.areProductsSame(this.selectedSopRegimen.get('night'), this.nightProducts)) {
      this.regimen.set('disableFollowUpSop', false);
      return;
    }
    this.regimen.set('disableFollowUpSop', true);
  }

  private areProductsSame(oldProducts: Array<any>, newProducts: Array<any>): boolean {
    if (oldProducts.length !== newProducts.length) {
      return false;
    }
    let productsMatched = true;
    oldProducts.forEach((productWithInstruction: any, index: number) => {
      if (productWithInstruction.product?.id !== newProducts[index].product.id
        || productWithInstruction.creationId !== newProducts[index].creationId) {
        productsMatched = false;
      }
    });
    return productsMatched;
  }
  moveProduct(event: CdkDragDrop<string[]>): void {
    if (this.disableEdit) {
      this.broadcaster.broadcast('NOTIFY', { message: 'Edit Not Allowed',
        type: this.appConfig.Shared.Toast.Type.ERROR });
      return;
    }
    if (!this.checkRegimenEditAllowedUnderNPD()) return;
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
    }
  }
  onEditRegimen(): void {
    this.openEditPopUp();
  }
  async openEditPopUp(): Promise<void> {
    const editReason = this.dialog.open(DataUploadModal,
      { data: {
        title: 'Reason for editing regimen',
        minCharLength: 10,
      } });
    editReason.afterClosed().subscribe((result: any) => {
      if (!result) {
        return;
      }
      if (result) {
        this.disableEdit = false;
        const reason = {
          editReason: 'OTHER',
          editDetails: result,
        };
        this.regimen.set('regimenEditReason', reason);
      }
    });
  }

  async copyRegimen(regimenId_: string): Promise<any> {
    let regimenId = regimenId_;
    try {
      if (regimenId.includes('personalized')) {
        await Promise.reject(new Error('Can\'t use personalized regimen to copy'));
      }
      if (regimenId.split('_').length === 1) {
        regimenId = `${this.regimen.get('regimenId').split('_')[0]}_${regimenId}`;
      }
      this.selectedSopRegimen = await this.connectionService.findOneRegimen({
        where: { regimenId },
        include: [
          'morning.product' as 'morning',
          'night.product' as 'night',
          'morning.product.instructions.frequencyOfApplicationLanguageString' as 'morning',
          'night.product.instructions.frequencyOfApplicationLanguageString' as 'night',
          'morning.product.instructions.areaOfApplicationLanguageString' as 'morning',
          'night.product.instructions.areaOfApplicationLanguageString' as 'night',
          'morning.product.instructions.quantityUnitLanguageString' as 'morning',
          'night.product.instructions.quantityUnitLanguageString' as 'night',
          'morning.product.instructions.durationOfApplicationLanguageString' as 'morning',
          'night.product.instructions.durationOfApplicationLanguageString' as 'night',
          'morning.instructionSet.frequencyOfApplicationLanguageString' as 'morning',
          'night.instructionSet.frequencyOfApplicationLanguageString' as 'night',
          'morning.instructionSet.areaOfApplicationLanguageString' as 'morning',
          'night.instructionSet.areaOfApplicationLanguageString' as 'night',
          'morning.instructionSet.quantityUnitLanguageString' as 'morning',
          'night.instructionSet.quantityUnitLanguageString' as 'night',
          'variants' as 'variants',
          'variants.morning' as 'variants',
          'variants.night' as 'variants',
          'variants.products' as 'variants',
          'variants.combinedProductIds' as 'variants',
          'variants.followUpConfig' as 'variants',
          'variants.tags' as 'variants',
          'variants.followUpSop' as 'variants',
        ],
        project: ['morning', 'night', 'products', 'class', 'variants', 'treatmentScenarioTree' ],
      });
      if (!this.selectedSopRegimen) await Promise.reject(new Error('No Regimen Found'));
      await this.copyFromRegimen(this.selectedSopRegimen);
      await this.updateRegimenPriceFromCentralizedPrice();
    } catch (err) {
      alert(err.message || err);
    }
  }

  async copyFromRegimen(regimen: any): Promise<void> {
    const fieldsToSkip = [
      'regimenId',
      'class',
      'type',
      'objectId',
      'ACL',
      'endDate',
      'activeFromDate',
      'createdAt',
      'updatedAt',
      'active',
      'followUpConfig',
      'numberOfProductsLimit',
    ];
    if (this.regimen.has('forUser')) {
      fieldsToSkip.push('fixedPrice', 'fixedPriceMRP', 'invoiceTitle');
    }
    [].concat(Object.keys(JSON.parse(JSON.stringify(this.regimen))))
      .concat(Object.keys(JSON.parse(JSON.stringify(regimen))))
      .filter((key: string) => !fieldsToSkip.includes(key))
      .forEach((key: string) => this.regimen.set(key, regimen.get(key)));
    const currentRegimen = this.regimen;
      if(!this.regimen.get('variants').find((variant: any) => variant.variantId === this.userSelectedVariantId)) {
        this.variantNotFound.emit({ variantNotFound: true, userSelectedVariantId: this.userSelectedVariantId, regimenId: regimen.get('regimenId') || '' });
        return;
      }
      const variantObject = regimen.get('variants').find((variant: any) => variant.variantId === this.userSelectedVariantId);
      Object.keys(variantObject).filter((key: string) => !fieldsToSkip.includes(key)).forEach((key: ParseKeys<Table.Regimen>) => currentRegimen.set(key, variantObject[key] as any));
        if(variantObject.variantId === ApiClientConstant.Regimen.SelectedVariantId.variant_1999) {
          currentRegimen.unset('progressiveInfo');
      }
    this.isRegimenSelected.emit(regimen.get('regimenId'));
    this.regimen = new Table.Regimen();
    this.regimen = currentRegimen;
    this.morningProducts = currentRegimen.get('morning');
    this.nightProducts = currentRegimen.get('night');
    this.progressiveInfo = currentRegimen.get('progressiveInfo');
  }

  async onChangeRegimen(regimen: any): Promise<void> {
    const copyFromRegimenId = regimen.get('regimenId');
    this.isSelectedRegimen = true;
    setTimeout(async () => {
      await this.copyRegimen(copyFromRegimenId);
      this.regimenUpdated.emit(this.regimen);
    }, 1000);
  }

  async onSaveRegimen(): Promise<any> {
    await this.saveRegimenOrderApproval();
  }

  async changeRegimenOnBasisOfTreatmentOutcome(changes: Array<FollowUpSopChange>, onHoldDuration: number): Promise<void> {
    this.onHoldProductsPayload.onHoldDurationInDays = onHoldDuration;
    this.morningProducts = this.regimen.get('morning').map((morningProduct: any) => ({ ...morningProduct }));
    this.nightProducts = this.regimen.get('night').map((nightProduct: any) => ({ ...nightProduct }));
    if (!changes?.length) return;
    await this.updateMorningNightProductFromRegimenSopLib(changes as Array<FollowUpSopChangeType>);
  }

  toggleRegimenActive(active: boolean): void {
    this.regimen.set('active', active);
  }

  checkRegimenEditAllowedUnderNPD(): boolean {
    if (this.npdExperimentEnabled && !this.medicalNoteAdded) {
      this.broadcaster.broadcast('NOTIFY', { message: 'Patient is under NPD Experiment, please add a medical Note to edit regimen',
        type: this.appConfig.Shared.Toast.Type.ERROR });
    }
    return true;
  }

  private findUniqueProducts(regimen: Table.Regimen): Array<Table.Catalog> {
    const uniqueProducts = {};
    return [
      ...regimen.get('morning'),
      ...regimen.get('night'),
    ]
      .filter(({ product }: MorningAndNightProductType) => {
        const id = (product.id || (product as { objectId?: string; }).objectId);
        if (!product || uniqueProducts[id]) { return false; }
        uniqueProducts[id] = product;
        return true;
      })
      .map(({ product }: any) => {
        const id = (product.id || product.objectId);
        const catalog = new Table.Catalog();
        catalog.id = id;
        return catalog;
      });
  }
  private async updateRegimenPriceFromCentralizedPrice() {
    const experiment = await this.connectionService.findExperiment({
      where: {
        name: 'centralised_regimen_variant_pricing',
        maxHashCode: { $gte: this.user.get('mod20') },
        minHashCode: { $lte: this.user.get('mod20') },
        enable: true,
      },
    });
    if (!experiment) {
      return;
    }
    const variants = this.regimen.get('variants');
    const experimentVariant = JSON.parse(experiment.get('variant')) || {};
    variants.forEach((variant) => {
      const { fixedPriceMRP, fixedPrice, revenueMultiplier } = this.getEffectivePriceDetailsForVariantId(experimentVariant[this.regimen.get('class')], variant)
      variant.fixedPrice = fixedPrice;
      variant.fixedPriceMRP = fixedPriceMRP;
      variant.revenueMultiplier = revenueMultiplier;
    })
    this.regimen.set('variants', variants);
  }

  private getEffectivePriceDetailsForVariantId(experimentVariant: { [key: `variant_${number}`]
      : { fixedPriceMRP: number, fixedPrice: number, revenueMultiplier: number } }, variant: Table.Regimen['attributes']['variants'][number])
    : { fixedPriceMRP: number, fixedPrice: number, revenueMultiplier: number } {
    return experimentVariant[variant.variantId as string]
      || { fixedPrice: variant.fixedPrice, fixedPriceMRP: variant.fixedPriceMRP, revenueMultiplier: variant.revenueMultiplier };
  }

  private async updateMorningNightProductFromRegimenSopLib(changes: Array<FollowUpSopChangeType>): Promise<void> {
    const morningProductJSON = JSON.parse(JSON.stringify(this.morningProducts));
    const nightProductJSON = JSON.parse(JSON.stringify(this.nightProducts));
    console.log('changes', changes);
    try {
      const { morning, night } = await this.regimenSopServices.changeRegimen(morningProductJSON, nightProductJSON, changes, FollowUpSopCallback as unknown as FollowUpSopCallback);
      this.morningProducts = this.updateObjectInParseObject(morning as Table.Regimen['attributes']['morning']);
      this.nightProducts = this.updateObjectInParseObject(night as Table.Regimen['attributes']['night']);
    } catch (error) {
      this.disableEdit = false;
      this.regimen.set('disableFollowUpSop', true);
      this.broadcaster.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }

  private convertDictionaryToParse(dictionaryObj: any, className: string): any {
    const parseObj = new Table[className]();
    Object.keys(dictionaryObj).filter((key: string) => (dictionaryObj[key] !== parseObj.get(key)))
      .forEach((key: string) => parseObj.set(key, dictionaryObj[key]));
    return parseObj;
  }

  private updateObjectInParseObject(updatedObject: Table.Regimen['attributes']['morning']): Table.Regimen['attributes']['morning'] {
    updatedObject.forEach((each) => {
      each.product = this.convertDictionaryToParse(each.product, 'Catalog');
      each.purposeLanguageString = this.convertDictionaryToParse(each.purposeLanguageString, 'LanguageString');
      each.purposeDescriptionLanguageString = this.convertDictionaryToParse(each.purposeDescriptionLanguageString, 'LanguageString');
      each.instructionSet.quantityUnitLanguageString = this.convertDictionaryToParse(each.instructionSet.quantityUnitLanguageString, 'LanguageString');
      each.instructionSet.areaOfApplicationLanguageString = this.convertDictionaryToParse(each.instructionSet.areaOfApplicationLanguageString, 'LanguageString');
      each.instructionSet.frequencyOfApplicationLanguageString = this.convertDictionaryToParse(each.instructionSet.frequencyOfApplicationLanguageString, 'LanguageString');
      each.instructionSet.durationOfApplicationLanguageString = this.convertDictionaryToParse(each.instructionSet.durationOfApplicationLanguageString, 'LanguageString');
      each.instructionSet.durationOfProductLanguageString = this.convertDictionaryToParse(each.instructionSet.durationOfProductLanguageString, 'LanguageString');
      each.instructionSet.notesLanguageStringTags = each.instructionSet.notesLanguageStringTags?.map((noteTag) => this.convertDictionaryToParse(noteTag, 'LanguageStringTag'));
      each.instructionSet.notesLanguageString = each.instructionSet.notesLanguageString?.map((note) => this.convertDictionaryToParse(note, 'LanguageString'));
      // @ts-ignore
      each.instructionSet.warningsLanguageString = each.instructionSet.warningsLanguageString?.map((warning) => this.convertDictionaryToParse(warning, 'LanguageString'));
    });
    return updatedObject;
  }
}
